// محافظ Route بر پایه Permission و Feature (AP-008): بدون Permission ← «دسترسی ندارید»؛ Feature غیرفعال ← پیشنهاد ارتقا.
import Button from '@mui/material/Button';
import { useTranslation } from '@phoenix/i18n';
import { EmptyState, ErrorState, LoadingState, UpgradeBadge } from '@phoenix/ui';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { useAccess } from './AccessProvider';

export interface RequireAccessProps {
  permission?: string;
  anyPermission?: readonly string[];
  feature?: string;
  /** مسیر صفحه اشتراک برای Feature غیرفعال. */
  upgradePath?: string;
  children: ReactNode;
}

export function ForbiddenState() {
  const { t } = useTranslation('shell');
  return (
    <div data-screen-id="STAFF-FORBIDDEN">
      <EmptyState
        titleKey="shell:pages.forbidden.title"
        descriptionKey="shell:pages.forbidden.description"
        action={
          <Button component={RouterLink} to="/" variant="outlined">
            {t('shell:pages.back_home')}
          </Button>
        }
      />
    </div>
  );
}

export function UpgradeRequiredState({ feature, upgradePath }: { feature: string; upgradePath?: string }) {
  const { t } = useTranslation('shell');
  return (
    <div data-screen-id="STAFF-UPGRADE-REQUIRED">
      <EmptyState
        titleKey="shell:pages.upgrade.title"
        descriptionKey="shell:pages.upgrade.description"
        icon={<UpgradeBadge feature={feature} />}
        action={
          upgradePath ? (
            <Button component={RouterLink} to={upgradePath} variant="contained" data-testid="upgrade-link">
              {t('common:actions.upgrade')}
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}

export function RequireAccess({
  permission,
  anyPermission,
  feature,
  upgradePath,
  children,
}: RequireAccessProps) {
  const access = useAccess();
  if (access.isLoading) {
    return <LoadingState />;
  }
  if (access.error) {
    return <ErrorState error={access.error} onRetry={access.refetch} />;
  }
  if (permission && !access.can(permission)) {
    return <ForbiddenState />;
  }
  if (anyPermission && !anyPermission.some((p) => access.can(p))) {
    return <ForbiddenState />;
  }
  if (feature && !access.featureEnabled(feature)) {
    return <UpgradeRequiredState feature={feature} upgradePath={upgradePath} />;
  }
  return <>{children}</>;
}
